import { useTranslation } from 'react-i18next';
import { format, subDays } from 'date-fns';
import { useState } from 'react';
import { Segmented } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { useGetAverages, useRpmPatientCharts } from 'infrastructure/hooks/rpm';
import { makeid } from 'utils/helpers/makeid';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import { usePatientThresholds } from 'infrastructure/hooks/patient/use-patient-thresholds';
import Charts from 'components/templates/rpm/patients/patient/charts';
import { DateFormats } from 'infrastructure/enums/date-formats';
import BaseButton from 'components/atoms/baseButton';

import s from './styles.module.scss';
import AverageItem from './components/average-item';

import type { FC } from 'react';

interface PatientAveragesProps {
  patientGuid: string;
  endTime?: number;
  patientDetailsLoading?: boolean;
}

const PatientAverages: FC<PatientAveragesProps> = ({
  patientGuid,
  endTime,
  patientDetailsLoading,
}) => {
  const [activeTab, setActiveTab] = useState<'averages' | 'realtime'>(
    'averages',
  );
  const [isExpanded, setIsExpanded] = useState(true);
  const { averages, isLoading } = useGetAverages({
    patientGuid,
    endTime,
  });
  const { charts, isLoading: isChartsLoading } = useRpmPatientCharts({
    patientGuid,
  });
  const { thresholds, loading } = usePatientThresholds({ patientGuid });
  const { t } = useTranslation();

  const today = new Date();
  const thirtyDaysAgo = subDays(today, 29);
  const ninetyDaysAgo = subDays(today, 89);

  const dateRange30 = `${format(
    thirtyDaysAgo,
    DateFormats['MMM d'],
  )} - ${format(today, DateFormats['MMM d'])}`;
  const dateRange90 = `${format(
    ninetyDaysAgo,
    DateFormats['MMM d'],
  )} - ${format(today, DateFormats['MMM d'])}`;

  return (
    <div className={s.wrapper} id="average-metrics">
      <div className={s['header-wrapper']}>
        <BaseButton
          type="secondary"
          className={s['toggle-button']}
          icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
          onClick={() => setIsExpanded(!isExpanded)}
        />
        <h4 className={s.title}>{t('labels.averageAndTrends')}</h4>
        <Segmented
          options={[
            {
              label: t('labels.average'),
              value: 'averages',
            },
            {
              label: t('labels.readingsGraph'),
              value: 'realtime',
            },
          ]}
          value={activeTab}
          onChange={(value) => setActiveTab(value as 'averages' | 'realtime')}
        />
      </div>
      {isExpanded && (
        <div className={s.body}>
          {activeTab === 'averages' ? (
            <>
              <BaseSpinWrapper spinning={isLoading || loading}>
                <div className={s['item-wrapper']}>
                  <h5 className={s.subtitle}>
                    {t('labels.averageLast30Days')}{' '}
                    <span className={s['date-range']}>({dateRange30})</span>
                  </h5>
                  <div className={s.items}>
                    {averages?.averages30.map((average) => (
                      <AverageItem
                        average={average}
                        thresholds={thresholds}
                        key={makeid(5)}
                      />
                    ))}
                  </div>
                  <div className={s.divider} />
                </div>
              </BaseSpinWrapper>
              <BaseSpinWrapper spinning={isLoading || loading}>
                <div className={s['item-wrapper']}>
                  <h5 className={s.subtitle}>
                    {t('labels.averageLast90Days')}{' '}
                    <span className={s['date-range']}>({dateRange90})</span>
                  </h5>
                  <div className={s.items}>
                    {averages?.averages90.map((average) => (
                      <AverageItem
                        average={average}
                        thresholds={thresholds}
                        key={makeid(5)}
                      />
                    ))}
                  </div>
                </div>
              </BaseSpinWrapper>
            </>
          ) : (
            <div className={s['graph-wrapper']}>
              <Charts
                charts={charts}
                patientDetailsLoading={patientDetailsLoading}
                chartsLoading={isChartsLoading}
                thresholds={thresholds}
                className={s['graph-container']}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
export default PatientAverages;
